import { useEffect, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { Plus, X, ScrollText, Trash2 } from "lucide-react";
import { fmtCurrency, fmtDate } from "@/lib/constants";

export default function Invoices() {
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const [i, c] = await Promise.all([api.get("/invoices"), api.get("/customers")]);
      setList(i.data); setCustomers(c.data);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const pay = async (id) => {
    const amt = prompt("Payment amount?");
    if (!amt) return;
    try { await api.post(`/invoices/${id}/payment`, { amount: Number(amt), method: "cash" }); await load(); }
    catch (e) { alert(formatApiError(e)); }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
      <header className="mb-6 flex items-end justify-between">
        <div>
          <p className="label-eyebrow text-primary">Billing</p>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight">Invoices</h1>
          <p className="mt-1 text-sm text-muted-foreground">GST-ready invoicing with payment tracking.</p>
        </div>
        <button data-testid="invoice-create-button" onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90">
          <Plus className="h-3.5 w-3.5" /> New invoice
        </button>
      </header>

      {err && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}

      <div className="sharp-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-border/60 bg-secondary/30 text-left">
            <tr>
              <th className="px-4 py-2 label-eyebrow">Invoice</th>
              <th className="px-4 py-2 label-eyebrow">Customer</th>
              <th className="px-4 py-2 label-eyebrow">Total</th>
              <th className="px-4 py-2 label-eyebrow">Paid</th>
              <th className="px-4 py-2 label-eyebrow">Balance</th>
              <th className="px-4 py-2 label-eyebrow">Status</th>
              <th className="px-4 py-2 label-eyebrow">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((i) => (
              <tr key={i.id} data-testid={`invoice-row-${i.invoice_no}`} className="border-b border-border/40 hover:bg-secondary/40">
                <td className="px-4 py-2">
                  <p className="font-mono text-xs">{i.invoice_no}</p>
                  <p className="text-[11px] text-muted-foreground">{fmtDate(i.created_at)}</p>
                </td>
                <td className="px-4 py-2">{i.customer_name || "—"}</td>
                <td className="px-4 py-2 font-medium">{fmtCurrency(i.total)}</td>
                <td className="px-4 py-2 text-emerald-400">{fmtCurrency(i.paid)}</td>
                <td className="px-4 py-2 text-red-400">{fmtCurrency(i.balance)}</td>
                <td className="px-4 py-2">
                  <span className={`inline-flex rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider
                    ${i.status === "paid" ? "bg-emerald-500/10 text-emerald-400" : i.status === "partial" ? "bg-amber-500/10 text-amber-400" : "bg-red-500/10 text-red-400"}`}>
                    {i.status}
                  </span>
                </td>
                <td className="px-4 py-2">
                  <div className="flex gap-1">
                    <button data-testid={`invoice-pay-${i.invoice_no}`} onClick={() => pay(i.id)} className="rounded-md border border-border/70 px-2 py-1 text-xs">Record payment</button>
                    <button
                      data-testid={`invoice-delete-${i.invoice_no}`}
                      onClick={async () => {
                        if (!window.confirm(`Delete invoice ${i.invoice_no}?`)) return;
                        try { await api.delete(`/invoices/${i.id}`); await load(); }
                        catch (e) { setErr(formatApiError(e)); }
                      }}
                      className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-xs text-muted-foreground">No invoices yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {creating && <InvoiceModal customers={customers} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function InvoiceModal({ customers, onClose, onSaved }) {
  const [customerId, setCustomerId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerGst, setCustomerGst] = useState("");
  const [items, setItems] = useState([{ description: "", quantity: 1, unit_price: 0, tax_percent: 18 }]);
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);

  const updateItem = (i, k, v) => {
    const copy = [...items]; copy[i] = { ...copy[i], [k]: v }; setItems(copy);
  };
  const addRow = () => setItems([...items, { description: "", quantity: 1, unit_price: 0, tax_percent: 18 }]);
  const removeRow = (i) => setItems(items.filter((_, x) => x !== i));

  const subtotal = items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
  const tax = items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0) * (Number(it.tax_percent) || 0) / 100, 0);
  const total = subtotal + tax;

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await api.post("/invoices", {
        customer_id: customerId || null,
        customer_name: customerId ? null : customerName,
        customer_gst: customerId ? null : customerGst,
        items: items.map((it) => ({ ...it, quantity: Number(it.quantity), unit_price: Number(it.unit_price), tax_percent: Number(it.tax_percent) })),
        notes,
      });
      onSaved();
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-3xl sharp-card">
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <h3 className="font-heading text-lg font-semibold">New invoice</h3>
          <button onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4 p-5">
          <div>
            <label className="label-eyebrow mb-1.5 block">Customer {customerId ? "" : "(or enter manually below)"}</label>
            <select data-testid="invoice-form-customer" value={customerId} onChange={(e) => setCustomerId(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="">— Not in customer list —</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
            {!customerId && (
              <div className="mt-2 grid grid-cols-2 gap-2">
                <input data-testid="invoice-form-customer-name" required value={customerName} onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Customer / company name" className="rounded-md border border-input bg-background px-3 py-2 text-sm" />
                <input data-testid="invoice-form-customer-gst" value={customerGst} onChange={(e) => setCustomerGst(e.target.value)}
                  placeholder="GST number (optional)" className="rounded-md border border-input bg-background px-3 py-2 text-sm" />
              </div>
            )}
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="label-eyebrow">Line items</p>
              <button type="button" onClick={addRow} className="inline-flex items-center gap-1 rounded-md border border-border/70 px-2 py-1 text-xs">
                <Plus className="h-3 w-3" /> Add row
              </button>
            </div>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2">
                  <input placeholder="Description" value={it.description} onChange={(e) => updateItem(i, "description", e.target.value)} className="col-span-5 rounded-md border border-input bg-background px-2 py-1.5 text-xs" required />
                  <input type="number" step="0.01" placeholder="Qty" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} className="col-span-2 rounded-md border border-input bg-background px-2 py-1.5 text-xs" />
                  <input type="number" step="0.01" placeholder="Price" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} className="col-span-2 rounded-md border border-input bg-background px-2 py-1.5 text-xs" />
                  <input type="number" step="0.01" placeholder="Tax %" value={it.tax_percent} onChange={(e) => updateItem(i, "tax_percent", e.target.value)} className="col-span-2 rounded-md border border-input bg-background px-2 py-1.5 text-xs" />
                  <button type="button" onClick={() => removeRow(i)} className="col-span-1 rounded-md border border-border/70 text-muted-foreground hover:text-red-400"><Trash2 className="mx-auto h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-md border border-border/60 bg-secondary/30 p-3 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>{fmtCurrency(subtotal)}</span></div>
            <div className="flex justify-between text-muted-foreground"><span>Tax</span><span>{fmtCurrency(tax)}</span></div>
            <div className="mt-2 flex justify-between border-t border-border/60 pt-2 font-heading font-bold"><span>Total</span><span>{fmtCurrency(total)}</span></div>
          </div>

          {err && <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
            <button disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {busy ? "Saving…" : "Create invoice"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
